import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { NavBar, NavBarButton } from '../../components/NavBar'
import {
  fetchAgentResourceGrants,
  sendKillAgent,
  sendRetireAgent,
  sendRenameAgent,
  wsRequest,
} from '../../api/ws'
import './AgentSettingsScreen.css'

type Details = {
  jdID: string
  jdTitle: string
  jdMandate: string
  jdRequiredSkills: string
  harnessName: string
  modelName: string
  wdPath: string
  hiredOn: string
  creatorName: string
}

type NotifPrefs = {
  permission_requests: boolean
  mentions: boolean
}

const DEFAULT_PREFS: NotifPrefs = { permission_requests: true, mentions: true }

type ConfirmAction = 'kill' | 'retire'

export function AgentSettingsScreen({
  agentInstanceID,
  positionID,
  agentName: initialName,
  hostname,
}: {
  agentInstanceID: string
  positionID: string
  agentName: string
  hostname: string
}) {
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)
  const isTemp = useAppStore(
    (s) => s.agentInstances.find((a) => a.id === agentInstanceID)?.is_temp ?? false,
  )
const rulesCount = useAppStore(
    (s) =>
      s.rules.filter(
        (r) =>
          r.principal_scope_kind === 'position' &&
          r.principal_scope_value === positionID,
      ).length,
  )
  // Live name from the store so it updates after a sendRenameAgent push.
  const currentName = useAppStore(
    (s) => s.agentInstances.find((a) => a.id === agentInstanceID)?.name ?? initialName,
  )
  // Live host name (agent → host join) so a host rename reflects here without
  // re-navigating. The `hostname` prop is a nav param captured at push time and
  // goes stale after a rename; the store value is refreshed by hosts_list.
  const currentHostname = useAppStore((s) => {
    const a = s.agentInstances.find((a) => a.id === agentInstanceID)
    const h = a ? s.hosts.find((hh) => hh.host_id === a.host_id) : undefined
    return h?.hostname ?? hostname
  })

  const [details, setDetails] = useState<Details | null>(null)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [prefs, setPrefs] = useState<NotifPrefs>(DEFAULT_PREFS)
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const [resourceCount, setResourceCount] = useState<number | null>(null)

  // Per-agent voice (Option B): whether this agent is a Home Assistant voice
  // conversation agent, and its optional spoken name (else the JD title).
  const [voicePublished, setVoicePublished] = useState(false)
  const [voiceName, setVoiceName] = useState('')
  const [voiceLoaded, setVoiceLoaded] = useState(false)
  const [editingVoiceName, setEditingVoiceName] = useState(false)
  const [voiceNameDraft, setVoiceNameDraft] = useState('')

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false)
  const [editName, setEditName] = useState(currentName)

  // Confirm sheet state
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null)

  // Fetch agent instance details
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [instResp, posResp] = await Promise.all([
          wsRequest<{
            ai_agent_instance?: {
              ai_brain_model_id: string
              harness_id: number
              created_at: string
            }
            creator_name?: string
            error?: string
          }>(
            'get_ai_agent_instance',
            { id: agentInstanceID },
            'ai_agent_instance',
            { kind: 'ai_agent_instance', id: agentInstanceID, action: 'read' },
          ),
          wsRequest<{
            organization_position?: {
              agent_job_description_id?: string
              working_directory_id: string
              voice_published?: boolean
              voice_name?: string
            }
            error?: string
          }>(
            'get_organization_position',
            { id: positionID },
            'organization_position',
            { kind: 'organization_position', id: positionID, action: 'read' },
          ),
        ])
        if (cancelled) return
        if (!instResp.ai_agent_instance) throw new Error(instResp.error ?? 'failed to load agent')
        if (!posResp.organization_position) throw new Error(posResp.error ?? 'failed to load position')

        const inst = instResp.ai_agent_instance
        const pos = posResp.organization_position

        // Per-agent voice state loads from the position itself (available before
        // the follow-up fetches below), so the Voice section renders even if those
        // fail.
        setVoicePublished(!!pos.voice_published)
        setVoiceName(pos.voice_name ?? '')
        setVoiceLoaded(true)

        const [wdResp, harnessResp, modelResp, jdResp] = await Promise.all([
          wsRequest<{ working_directory?: { directory_path: string }; error?: string }>(
            'get_working_directory', { id: pos.working_directory_id }, 'working_directory',
            { kind: 'working_directory', id: pos.working_directory_id, action: 'read' },
          ),
          wsRequest<{ harness?: { name: string }; error?: string }>(
            'get_harness', { id: inst.harness_id }, 'harness',
            { kind: 'harness', id: String(inst.harness_id), action: 'read' },
          ),
          wsRequest<{ ai_brain_model?: { name: string }; error?: string }>(
            'get_ai_brain_model', { id: inst.ai_brain_model_id }, 'ai_brain_model',
            { kind: 'ai_brain_model', id: inst.ai_brain_model_id, action: 'read' },
          ),
          pos.agent_job_description_id
            ? wsRequest<{ agent_job_description?: { title: string; mandate?: string; required_skills?: string }; error?: string }>(
                'get_agent_job_description',
                { id: pos.agent_job_description_id },
                'agent_job_description',
                { kind: 'agent_job_description', id: pos.agent_job_description_id, action: 'read' },
              )
            : Promise.resolve({ agent_job_description: undefined } as {
                agent_job_description?: { title: string; mandate?: string; required_skills?: string }
              }),
        ])
        if (cancelled) return

        setDetails({
          jdID: pos.agent_job_description_id ?? '',
          jdTitle: jdResp.agent_job_description?.title ?? '—',
          jdMandate: jdResp.agent_job_description?.mandate ?? '',
          jdRequiredSkills: jdResp.agent_job_description?.required_skills ?? '',
          harnessName: harnessResp.harness?.name ?? '—',
          modelName: modelResp.ai_brain_model?.name ?? '—',
          wdPath: wdResp.working_directory?.directory_path ?? '—',
          hiredOn: formatDate(inst.created_at),
          creatorName: instResp.creator_name ?? 'Unknown',
        })
      } catch (err) {
        if (!cancelled) setDetailsError(err instanceof Error ? err.message : String(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [agentInstanceID, positionID])

  // Fetch notification preferences
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const resp = await wsRequest<{
          notification_preferences?: Array<{ ai_agent_instance_id: string } & NotifPrefs>
          error?: string
        }>('list_notification_preferences', {}, 'notification_preferences', {
          kind: 'notification_preference',
          action: 'list',
        })
        if (cancelled) return
        const row = resp.notification_preferences?.find(
          (r) => r.ai_agent_instance_id === agentInstanceID,
        )
        if (row) setPrefs({ permission_requests: !!row.permission_requests, mentions: !!row.mentions })
        setPrefsLoaded(true)
      } catch {
        if (!cancelled) setPrefsLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [agentInstanceID])

  // Fetch resource grant count for the summary row
  useEffect(() => {
    let cancelled = false
    fetchAgentResourceGrants(agentInstanceID)
      .then((resp) => { if (!cancelled) setResourceCount(resp.grants?.length ?? 0) })
      .catch(() => { if (!cancelled) setResourceCount(0) })
    return () => {
      cancelled = true
    }
  }, [agentInstanceID])

  function togglePref(key: keyof NotifPrefs) {
    const next = { ...prefs, [key]: !prefs[key] }
    setPrefs(next)
    void wsRequest(
      'set_notification_preference',
      { ai_agent_instance_id: agentInstanceID, ...next },
      'notification_preference_set',
      { kind: 'notification_preference', id: agentInstanceID, action: 'update' },
    ).catch(() => setPrefs(prefs))
  }

  function toggleVoicePublished() {
    const next = !voicePublished
    setVoicePublished(next) // optimistic; revert on failure
    void wsRequest(
      'update_organization_position',
      { id: positionID, voice_published: next },
      'organization_position_updated',
      { kind: 'organization_position', id: positionID, action: 'update' },
    ).catch(() => setVoicePublished(!next))
  }

  function saveVoiceName() {
    const trimmed = voiceNameDraft.trim()
    const prev = voiceName
    setVoiceName(trimmed)
    setEditingVoiceName(false)
    void wsRequest(
      'update_organization_position',
      { id: positionID, voice_name: trimmed },
      'organization_position_updated',
      { kind: 'organization_position', id: positionID, action: 'update' },
    ).catch(() => setVoiceName(prev))
  }

  function startRename() {
    setEditName(currentName)
    setIsRenaming(true)
  }

  function saveName() {
    const trimmed = editName.trim()
    if (!trimmed) return
    sendRenameAgent(agentInstanceID, trimmed)
    setIsRenaming(false)
  }

  function cancelRename() {
    setIsRenaming(false)
  }

  function handleConfirm(action: ConfirmAction) {
    setConfirm(null)
    if (action === 'kill') {
      // Safety action: surface a dropped kill frame rather than let it look done.
      if (!sendKillAgent(agentInstanceID)) {
        window.alert(
          "Couldn't reach the server to unplug this agent — it was NOT stopped. Check your connection and try again.",
        )
      }
    } else {
      sendRetireAgent(agentInstanceID)
      popNav() // settings
      popNav() // agent detail
    }
  }

  const rulesLabel = rulesCount === 0 ? 'None' : `${rulesCount} ${rulesCount === 1 ? 'rule' : 'rules'}`
  const resourceLabel = resourceCount === null ? '—' : resourceCount === 0 ? 'None' : `${resourceCount} connected`

  return (
    <div className="agent-settings">
      <NavBar
        title="Settings"
        leading={<NavBarButton icon="‹" label="Back" onClick={popNav} />}
      />

      <div className="agent-settings__scroll">
        <SectionHeader>About</SectionHeader>
        <Group>
          {isRenaming ? (
            <div className="agent-settings__row">
              <span className="agent-settings__row-label">Name</span>
              <div className="agent-settings__rename">
                <input
                  className="agent-settings__rename-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName() }}
                />
                <button
                  className="agent-settings__rename-save"
                  onClick={saveName}
                  disabled={!editName.trim()}
                >
                  Save
                </button>
                <button className="agent-settings__rename-cancel" onClick={cancelRename}>
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <NavRow label="Name" value={currentName} onClick={startRename} />
          )}

          {detailsError ? (
            <div className="agent-settings__error">{detailsError}</div>
          ) : !details ? (
            <div className="agent-settings__loading">Loading…</div>
          ) : (
            <>
              <NavRow
                label="Role"
                value={details.jdTitle}
                onClick={() =>
                  pushNav({
                    kind: 'agent_mandate',
                    title: details.jdTitle,
                    mandate: details.jdMandate,
                    jobDescriptionID: details.jdID,
                    requiredSkills: details.jdRequiredSkills,
                  })
                }
              />
              <InfoRow label="Host" value={currentHostname} />
              <InfoRow label="Harness" value={details.harnessName} />
              <InfoRow label="Model" value={details.modelName} />
              <NavRow
                label="Working Dir"
                value={details.wdPath}
                valueMono
                icon="⧉"
                onClick={() => navigator.clipboard?.writeText(details.wdPath).catch(() => {})}
              />
              <InfoRow label="Hired on" value={details.hiredOn} />
              <InfoRow label="Hired by" value={details.creatorName} />
            </>
          )}
        </Group>

        <SectionHeader>Automation</SectionHeader>
        <Group>
          <NavRow
            label="Smart Rules"
            value={rulesLabel}
            onClick={() => pushNav({ kind: 'agent_grants', positionID })}
          />
        </Group>

        <SectionHeader>Resources & Skills</SectionHeader>
        <Group>
          <NavRow
            label="Connections"
            value={resourceLabel}
            onClick={() => pushNav({ kind: 'agent_resources', agentInstanceID })}
          />
        </Group>

        {voiceLoaded && (
          <>
            <SectionHeader>Voice</SectionHeader>
            <Group>
              <ToggleRow
                label="Reachable by voice"
                checked={voicePublished}
                onChange={toggleVoicePublished}
              />
              {voicePublished &&
                (editingVoiceName ? (
                  <div className="agent-settings__row">
                    <span className="agent-settings__row-label">Spoken name</span>
                    <div className="agent-settings__rename">
                      <input
                        className="agent-settings__rename-input"
                        value={voiceNameDraft}
                        onChange={(e) => setVoiceNameDraft(e.target.value)}
                        placeholder={details?.jdTitle ?? 'Role name'}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveVoiceName()
                        }}
                      />
                      <button className="agent-settings__rename-save" onClick={saveVoiceName}>
                        Save
                      </button>
                      <button
                        className="agent-settings__rename-cancel"
                        onClick={() => setEditingVoiceName(false)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <NavRow
                    label="Spoken name"
                    value={voiceName || `${details?.jdTitle ?? 'Role name'} (default)`}
                    onClick={() => {
                      setVoiceNameDraft(voiceName)
                      setEditingVoiceName(true)
                    }}
                  />
                ))}
            </Group>
          </>
        )}

        {prefsLoaded && (
          <>
            <SectionHeader>Notifications</SectionHeader>
            <Group>
              <ToggleRow
                label="Permission requests"
                checked={prefs.permission_requests}
                onChange={() => togglePref('permission_requests')}
              />
              <ToggleRow
                label="@mentions of me"
                checked={prefs.mentions}
                onChange={() => togglePref('mentions')}
              />
            </Group>
          </>
        )}

        <SectionHeader>Actions</SectionHeader>
        <Group>
          <button
            className="agent-settings__row agent-settings__row--tappable agent-settings__row--danger"
            onClick={() => setConfirm('kill')}
          >
            Pull the Plug
          </button>
          <button
            className="agent-settings__row agent-settings__row--tappable agent-settings__row--danger"
            onClick={() => setConfirm('retire')}
          >
            {isTemp ? 'Fire Agent' : 'Retire Agent'}
          </button>
        </Group>
      </div>

      {confirm && (
        <div className="agent-settings__confirm" onClick={() => setConfirm(null)}>
          <div
            className="agent-settings__confirm-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agent-settings__confirm-title">
              {confirm === 'kill'
                ? 'Kill the running agent process immediately?'
                : isTemp
                  ? 'Fire this agent? It will be removed from your list.'
                  : 'Retire this agent? It will be removed from your list.'}
            </div>
            <button
              className="agent-settings__confirm-destructive"
              onClick={() => handleConfirm(confirm)}
            >
              {confirm === 'kill' ? 'Pull the Plug' : isTemp ? 'Fire Agent' : 'Retire Agent'}
            </button>
          </div>
          <button
            className="agent-settings__confirm-cancel"
            onClick={() => setConfirm(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div className="agent-settings__section-header">{children}</div>
}

function Group({ children }: { children: React.ReactNode }) {
  return <div className="agent-settings__group">{children}</div>
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="agent-settings__row">
      <span className="agent-settings__row-label">{label}</span>
      <span className="agent-settings__row-value">{value}</span>
    </div>
  )
}

function NavRow({
  label,
  value,
  valueMono,
  icon,
  onClick,
}: {
  label: string
  value: string
  valueMono?: boolean
  icon?: string
  onClick: () => void
}) {
  return (
    <button className="agent-settings__row agent-settings__row--tappable" onClick={onClick}>
      <span className="agent-settings__row-label">{label}</span>
      <span
        className={`agent-settings__row-value${valueMono ? ' agent-settings__row-value--mono' : ''}`}
      >
        {value}
      </span>
      {icon ? (
        <span className="agent-settings__row-icon">{icon}</span>
      ) : (
        <span className="agent-settings__row-chevron">›</span>
      )}
    </button>
  )
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label className="agent-settings__row agent-settings__row--tappable">
      <span className="agent-settings__row-label">{label}</span>
      <span className="agent-settings__toggle">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="agent-settings__toggle-track" />
      </span>
    </label>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
